"use client";

import { Text } from "@zapier/design-system";
import { Template } from "src/types";
import styles from "./AppsSection.module.css";
import { useAppDetailsBySlugs } from "src/hooks";
import { filterBuiltInApps } from "src/utils";

type Props = {
  template: Template;
};

/**
 * AppsSection is a component that displays a list of apps that are used in a template.
 * Built-in apps (like "Filter by Zapier", "Webhooks by Zapier", etc.) are filtered out.
 */
export const AppsSection = ({ template }: Props) => {
  const apps = template.apps;
  if (!apps?.length) {
    return null;
  }

  const { data: appDetails, isLoading: isLoadingAppDetails } =
    useAppDetailsBySlugs({ slugs: apps });

  if (isLoadingAppDetails) {
    return null;
  }

  // Filter out built-in apps (e.g., "Filter by Zapier", "Webhooks by Zapier")
  const filteredApps = filterBuiltInApps(apps, appDetails);

  const getAppName = (app: string) => {
    return appDetails?.get(app)?.name ?? app;
  };

  // Don't render if no apps remain after filtering
  if (filteredApps.length === 0) {
    return null;
  }

  return (
    <section>
      <Text
        type="ParagraphHeader2"
        className={styles.sidebarSectionTitle}
        tag="h2"
      >
        Apps
      </Text>
      <ul className={styles.apps}>
        {filteredApps.map((app) => (
          <li className={styles.app} key={app}>
            <div className={styles.appIconContainer}>
              <img
                className={styles.appIcon}
                src={`https://zapier.com/generated/${encodeURIComponent(app)}/64`}
                alt={`${app} icon`}
              />
            </div>
            <Text type="Body3">{getAppName(app)}</Text>
          </li>
        ))}
      </ul>
    </section>
  );
};
